// src/views/source-connections/components/wizard/StepReview.jsx

import React from "react";
import { maskCredential, generateJobName } from "../../utils";

const StepReview = ({ sourceType, formData }) => {
  const isManual = sourceType.authMethod === "csv_upload";
  const jobName = generateJobName(sourceType, formData.connection_name);
  const selectedDataTypes = formData.dataTypes || sourceType.dataTypes.filter((d) => d.default).map((d) => d.id);
  const dataTypeLabels = selectedDataTypes.map((id) => sourceType.dataTypes.find((d) => d.id === id)?.label).filter(Boolean);

  return (
    <div className="p-6 overflow-y-auto" style={{ maxHeight: "60vh" }}>
      {/* Connection Details */}
      <div className="border border-card-bd rounded-card overflow-hidden mb-5">
        <div className="flex justify-between items-center px-4 py-3 bg-surface-2 border-b border-border">
          <span className="text-xs font-semibold uppercase tracking-wider text-text-2">Connection Details</span>
        </div>
        <div className="p-4 grid grid-cols-2 gap-4">
          <div>
            <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-1">Source</div>
            <div className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded ${sourceType.icon.bgColor} flex items-center justify-center text-[10px] font-bold ${sourceType.icon.textColor}`}>
                {sourceType.icon.letter}
              </div>
              <span className="text-sm font-medium text-text">{sourceType.name}</span>
            </div>
          </div>
          <div>
            <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-1">Connection Name</div>
            <div className="text-sm text-text">{formData.connection_name}</div>
          </div>
          <div>
            <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-1">Auth Method</div>
            <div className="text-sm text-text">{sourceType.credentialLabel}</div>
          </div>
          {formData.api_key && (
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-1">API Key</div>
              <div className="text-sm text-text font-mono">{maskCredential(formData.api_key)}</div>
            </div>
          )}
          {formData.file && (
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-1">Key File</div>
              <div className="text-sm text-text">{formData.file.name}</div>
            </div>
          )}
          {sourceType.fields
            .filter((f) => f.type !== "password" && f.type !== "select" && formData[f.name])
            .map((f) => (
              <div key={f.name}>
                <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-1">{f.label}</div>
                <div className="text-sm text-text">{formData[f.name]}</div>
              </div>
            ))}
          {formData.associated_provider && (
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-1">Associated Provider</div>
              <div className="text-sm text-text">{formData.associated_provider}</div>
            </div>
          )}
          {dataTypeLabels.length > 0 && (
            <div className="col-span-2">
              <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-1">Data to Import</div>
              <div className="text-sm text-text">{dataTypeLabels.join(", ")}</div>
            </div>
          )}
        </div>
      </div>

      {/* Import Job Preview */}
      {!isManual && (
        <div className="border border-card-bd rounded-card overflow-hidden mb-5">
          <div className="px-4 py-3 bg-surface-2 border-b border-border">
            <span className="text-xs font-semibold uppercase tracking-wider text-text-2">Import Job — Auto-created</span>
          </div>
          <div className="p-4 grid grid-cols-2 gap-4">
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-1">Job Name</div>
              <div className="text-sm text-text">{jobName}</div>
            </div>
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-1">Schedule</div>
              <div className="text-sm text-text">Daily at 02:00 UTC</div>
            </div>
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-1">Pipeline Stage</div>
              <div className="text-sm text-text">Import → Transform → Forecast</div>
            </div>
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-1">Status After Save</div>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium border bg-success-soft text-success border-success">Active</span>
            </div>
          </div>
          <div className="mx-4 mb-4 p-3 bg-surface-2 rounded-ctrl border border-border text-xs text-text-2">
            The import job will start its first run immediately after saving. Schedule and settings can be adjusted on the Data Imports page.
          </div>
        </div>
      )}

      {/* Validation Notice */}
      <div className="border border-card-bd rounded-card overflow-hidden">
        <div className="px-4 py-3 bg-surface-2 border-b border-border">
          <span className="text-xs font-semibold uppercase tracking-wider text-text-2">Credential Validation</span>
        </div>
        <div className="p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-surface-2 flex items-center justify-center border border-border">
            <span className="text-text-2">⏳</span>
          </div>
          <div>
            <div className="text-sm font-medium text-text">Validation will run after saving</div>
            <div className="text-xs text-text-2 mt-0.5">A background check will verify API access and permissions. Status visible on the connections overview.</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepReview;
